import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { PiColumnsPlusLeft } from "react-icons/pi";
import { GrConfigure } from 'react-icons/gr';
import './AdderNode.css'; // Import the CSS file
import AdderConfigModal from './AdderConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const isUnspecifiedAdderValue = (value, sentinelLabel) => {
  if (value === undefined || value === null) return true;
  const normalized = String(value).trim();
  return normalized === '' || normalized.toLowerCase() === sentinelLabel.toLowerCase();
};

const AdderNode = ({ id, data, selected }) => {
  const { inputA_col_name, inputB_col_name, sum_column_name, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addAdderNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node names if there are connections
  const getInputNodeNames = useCallback(() => {
    const inputEdge1 = edges.find(edge => edge.target === id && edge.targetHandle === 'input1');
    const inputEdge2 = edges.find(edge => edge.target === id && edge.targetHandle === 'input2');
    
    const result = {
      input1: null,
      input2: null
    };
    
    // Process first input
    if (inputEdge1) {
      const sourceNodeId1 = inputEdge1.source;
      const sourceNode1 = nodes.find(node => node.id === sourceNodeId1);
      
      if (sourceNode1 && sourceNode1.data && sourceNode1.data.name) {
        result.input1 = sourceNode1.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.input1 = nodeMapping[sourceNodeId1] || sourceNodeId1;
      }
    }
    
    // Process second input
    if (inputEdge2) {
      const sourceNodeId2 = inputEdge2.source;
      const sourceNode2 = nodes.find(node => node.id === sourceNodeId2);
      
      if (sourceNode2 && sourceNode2.data && sourceNode2.data.name) {
        result.input2 = sourceNode2.data.name;
      } else {
        const nodeMapping = getNodeMapping();
        result.input2 = nodeMapping[sourceNodeId2] || sourceNodeId2;
      }
    }
    
    console.log(`AdderNode ${id} input connections:`, result);
    
    return result;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Function to check if a stored input node name matches any current connection
  const hasMatchingInput = useCallback((storedNodeName) => {
    if (!storedNodeName) return true; // If no stored name, consider it matching
    
    const inputNodes = getInputNodeNames();
    return inputNodes.input1 === storedNodeName || inputNodes.input2 === storedNodeName;
  }, [getInputNodeNames]);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`AdderNode ${id} received data:`, data);
    // Log specific column name information for debugging
    console.log(`AdderNode ${id} column name details:`, {
      inputA_col_nameFromProps: inputA_col_name,
      inputA_col_nameFromData: data.inputA_col_name,
      inputB_col_nameFromProps: inputB_col_name,
      inputB_col_nameFromData: data.inputB_col_name,
      sum_column_nameFromProps: sum_column_name,
      sum_column_nameFromData: data.sum_column_name
    });
    
    // Log reconfiguration state
    console.log(`AdderNode ${id} reconfiguration state:`, {
      isConfigured: data.isConfigured,
      isApiConfigured: data.isApiConfigured,
      needsReconfiguration: data.needsReconfiguration,
      needsPartialReconfiguration: data.needsPartialReconfiguration
    });
  }, [id, data, inputA_col_name, inputB_col_name, sum_column_name]);

  // Check if node is configured properly
  // With auto-select, input columns may be blank; treat sum_column_name as required for display/semantics
  const isConfigured = data.isConfigured || (!!sum_column_name);
  
  // Check if the node needs reconfiguration
  // This happens when the node is configured but one of its inputs has changed
  // Or when the node is explicitly marked as needing reconfiguration
  const needsReconfiguration = data.needsReconfiguration || data.isApiConfigured === false || 
    (isConfigured && data.inputNode1Name && data.inputNode2Name && 
      (!hasMatchingInput(data.inputNode1Name) || !hasMatchingInput(data.inputNode2Name)));
      
  // Check if this node needs partial reconfiguration (one input was deleted)
  // Or when the node is explicitly marked as needing partial reconfiguration
  const needsPartialReconfiguration = data.needsPartialReconfiguration || 
    (isConfigured && ((data.inputNode1Name && !hasMatchingInput(data.inputNode1Name)) || 
                     (data.inputNode2Name && !hasMatchingInput(data.inputNode2Name))));

  // Check if this node has input connections
  const hasInputConnection1 = edges.some(edge => edge.target === id && edge.targetHandle === 'input1');
  const hasInputConnection2 = edges.some(edge => edge.target === id && edge.targetHandle === 'input2');
  const hasAllInputConnections = hasInputConnection1 && hasInputConnection2;
  const hasAtLeastOneInput = hasInputConnection1 || hasInputConnection2;
  
  // Log computed reconfiguration state for debugging
  useEffect(() => {
    console.log(`AdderNode ${id} computed reconfiguration state:`, {
      isConfigured,
      needsReconfiguration,
      needsPartialReconfiguration,
      hasInputConnection1,
      hasInputConnection2,
      hasAllInputConnections,
      hasAtLeastOneInput
    });
  }, [id, isConfigured, needsReconfiguration, needsPartialReconfiguration, 
      hasInputConnection1, hasInputConnection2, hasAllInputConnections, hasAtLeastOneInput]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  // Function to handle reconfiguration of the input nodes
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node names
      const inputNodes = getInputNodeNames();
      
      // Check if we're doing a partial reconfiguration (one input was deleted)
      const isPartialReconfiguration = needsPartialReconfiguration && 
        ((inputNodes.input1 && !inputNodes.input2) || (!inputNodes.input1 && inputNodes.input2));
      
      console.log(`Reconfiguring node ${name} with input nodes:`, inputNodes);
      console.log(`Partial reconfiguration: ${isPartialReconfiguration}`);
      
      // Update the UI first to provide immediate feedback
      const updateNodeUI = (input1Name, input2Name) => {
        // Update the node data
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false,
                  inputNode1Name: input1Name,
                  inputNode2Name: input2Name,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update the session state
        const position = getNodePosition();
        addAdderNode(
          name,
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          input1Name,
          input2Name,
          { x: position.x, y: position.y }
        );
      };
      
      if (isPartialReconfiguration) {
        // For partial reconfiguration, we only update the connected input
        if (inputNodes.input1) {
          try {
            // Only input1 is connected, update it
            await reconfigureNode(
              session.pipeline_name,
              name,
              inputNodes.input1,
              0  // inputIndex = 0 for the first input
            );
            console.log(`Node ${name} first input reconfigured to ${inputNodes.input1}`);
          } catch (error) {
            console.error('API error reconfiguring first input:', error);
            // Continue with UI update even if API call fails
            console.log('Continuing with UI update despite API error');
          }
          
          // Keep the previous value for input2 if it exists
          const input2Name = data.inputNode2Name || null;
          
          // Update the UI
          updateNodeUI(inputNodes.input1, input2Name);
          
        } else if (inputNodes.input2) {
          try {
            // Only input2 is connected, update it
            await reconfigureNode(
              session.pipeline_name,
              name,
              inputNodes.input2,
              1  // inputIndex = 1 for the second input
            );
            console.log(`Node ${name} second input reconfigured to ${inputNodes.input2}`);
          } catch (error) {
            console.error('API error reconfiguring second input:', error);
            // Continue with UI update even if API call fails
            console.log('Continuing with UI update despite API error');
          }
          
          // Keep the previous value for input1 if it exists
          const input1Name = data.inputNode1Name || null;
          
          // Update the UI
          updateNodeUI(input1Name, inputNodes.input2);
        }
      } else {
        // Regular reconfiguration with both inputs
        if (!inputNodes.input1 || !inputNodes.input2) {
          throw new Error('Both input nodes must be connected for full reconfiguration');
        }
        
        let apiError = null;
        
        try {
          // First, reconfigure with the first input (input1)
          await reconfigureNode(
            session.pipeline_name,
            name,
            inputNodes.input1,
            0  // inputIndex = 0 for the first input
          );
          console.log(`Node ${name} first input reconfigured to ${inputNodes.input1}`);
        } catch (error) {
          console.error('API error reconfiguring first input:', error);
          apiError = error;
        }
        
        try {
          // Then, reconfigure with the second input (input2)
          await reconfigureNode(
            session.pipeline_name,
            name,
            inputNodes.input2,
            1  // inputIndex = 1 for the second input
          );
          console.log(`Node ${name} second input reconfigured to ${inputNodes.input2}`);
        } catch (error) {
          console.error('API error reconfiguring second input:', error);
          if (!apiError) apiError = error;
        }
        
        // Update the UI even if API calls failed
        updateNodeUI(inputNodes.input1, inputNodes.input2);
        
        // If there was an API error, log it but don't alert the user since the UI is updated
        if (apiError) {
          console.warn('API errors occurred during reconfiguration, but UI was updated:', apiError);
        }
      }
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error in reconfiguration process:', error);
      // Update the UI anyway to prevent the node from being stuck in reconfiguration state
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                needsReconfiguration: false,
                needsPartialReconfiguration: false,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      setIsConfiguring(false);
    }
  };

  const openConfigModal = useCallback(() => {
    // For initial configuration, we need both inputs
    if (!isConfigured && !hasAllInputConnections) {
      // Show an alert if there's no input connection
      notifyError(new Error("Please connect both inputs to this adder node before configuring it."), {
        toastId: 'adder.connectBothInputs',
      });
      return;
    }
    
    // For reconfiguration, we need at least one input
    if ((needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) && !hasAtLeastOneInput) {
      notifyError(new Error("Please connect at least one input to this adder node before reconfiguring it."), {
        toastId: 'adder.connectOneInput',
      });
      return;
    }
    
    // If the node is already configured and the user is trying to edit it (not reconfigure)
    if (isConfigured && !needsReconfiguration && !needsPartialReconfiguration && data.isApiConfigured !== false) {
      // Open the modal in edit mode
      console.log("Opening modal for editing existing adder node");
      setIsModalOpen(true);
      return;
    }
    
    setIsModalOpen(true);
  }, [hasAllInputConnections, hasAtLeastOneInput, isConfigured, needsReconfiguration, needsPartialReconfiguration, data.isApiConfigured]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, inputA_col_name, inputB_col_name, sum_column_name } = configData;
      const inputNodes = getInputNodeNames();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring adder node ${id} -> ${name}:`, {
        configData,
        inputNodes,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id)
      });
      
      // Add the adder node to the session state
      if (inputNodes.input1 && inputNodes.input2) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        console.log(`Adding adder node to session:`, {
          name,
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          inputNode1Name: inputNodes.input1,
          inputNode2Name: inputNodes.input2,
          position
        });
        
        // Now add the node to the session with the correct name
        addAdderNode(
          name,
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          inputNodes.input1,
          inputNodes.input2,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  inputA_col_name,
                  inputB_col_name,
                  sum_column_name,
                  inputNode1Name: inputNodes.input1,
                  inputNode2Name: inputNodes.input2,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  inputA_col_name,
                  inputB_col_name,
                  sum_column_name,
                  inputNode1Name: inputNodes.input1 || null,
                  inputNode2Name: inputNodes.input2 || null,
                  isConfigured: true,
                  needsReconfiguration: false,
                  needsPartialReconfiguration: false
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring adder node:', error);
      notifyError(error, {
        operation: (needsReconfiguration || needsPartialReconfiguration) ? OP.node.reconfigure : OP.node.editAdder,
      });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reactFlowInstance, getInputNodeNames, getNodePosition, closeConfigModal, updateNodeMapping, addAdderNode, session, updateSelectedNode, edges]);

  return (
    <>
      <div
        className={`adder-node ${selected ? 'adder-selected' : 
                               (needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) ? 'adder-needs-reconfiguration' : 
                               isConfigured ? 'adder-configured' : 'adder-not-configured'}`}
      >
        <div className={`adder-node-header ${(needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false) ? 'adder-needs-reconfiguration' : 
                                           isConfigured ? 'adder-configured' : 'adder-not-configured'}`}>
          <div className="adder-node-header-title">
            <div className="adder-node-type">Adder</div>
            {name && (isConfigured || needsReconfiguration || needsPartialReconfiguration) && 
              <div className="adder-node-name-display">{name}</div>}
          </div>
          <div className="adder-node-header-icons">
            {/* Show different buttons based on node state */}
            {needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false ? (
              <button 
                onClick={handleReconfigureInput}
                className="adder-config-button adder-reconfigure-button"
                title="Reconfigure input nodes"
                disabled={(needsReconfiguration && !hasAllInputConnections && !needsPartialReconfiguration) || 
                         (needsPartialReconfiguration && !hasAtLeastOneInput) || 
                         isConfiguring}
              >
                <GrConfigure className="adder-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="adder-config-button"
                title={isConfigured ? "Edit Configuration" : hasAllInputConnections ? "Configure Adder" : "Connect both inputs first"}
                disabled={!hasAllInputConnections || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="adder-icon" />
                ) : (
                  <ExclamationTriangleIcon className="adder-icon" />
                )}
              </button>
            )}
            <PiColumnsPlusLeft className="adder-icon" />
          </div>
        </div>
        <div className="adder-node-content">
          <div className="adder-text-xs">
            {(!hasInputConnection1 || !hasInputConnection2) && (
              <div className="adder-text-warning">
                Connect both inputs to configure
              </div>
            )}
            {hasAllInputConnections && !isConfigured && (
              <div className="adder-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="adder-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>Input A Column: {
                  !isUnspecifiedAdderValue(inputA_col_name, 'Column A not specified') ? 
                    inputA_col_name : 
                    (!isUnspecifiedAdderValue(data.inputA_col_name, 'Column A not specified') ? 
                      data.inputA_col_name : 
                      'Auto')
                }</div>
                <div>Input B Column: {
                  !isUnspecifiedAdderValue(inputB_col_name, 'Column B not specified') ? 
                    inputB_col_name : 
                    (!isUnspecifiedAdderValue(data.inputB_col_name, 'Column B not specified') ? 
                      data.inputB_col_name : 
                      'Auto')
                }</div>
                <div>Sum Column: {
                  !isUnspecifiedAdderValue(sum_column_name, 'Sum column not specified') ? 
                    sum_column_name : 
                    (!isUnspecifiedAdderValue(data.sum_column_name, 'Sum column not specified') ? 
                      data.sum_column_name : 
                      'N/A')
                }</div>
              </>
            )}
          </div>
          <div className="adder-flex-container">
            <Handle
              type="target"
              position={Position.Left}
              id="input1"
              className={`adder-connection-point ${
                needsReconfiguration ? 'adder-needs-reconfiguration' : 
                isConfigured ? 'adder-configured' : 
                'adder-not-configured'
              }`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ top: '35%', left: '-4px' }} // Position slightly outward
            />
            <Handle
              type="target"
              position={Position.Left}
              id="input2"
              className={`adder-connection-point ${
                needsReconfiguration ? 'adder-needs-reconfiguration' : 
                isConfigured ? 'adder-configured' : 
                'adder-not-configured'
              }`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ top: '65%', left: '-4px' }} // Position slightly outward
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`adder-connection-point ${
                needsReconfiguration ? 'adder-needs-reconfiguration-output' : 
                isConfigured ? 'adder-configured-output' : 
                'adder-not-configured'
              }`}
              isConnectable={isConfigured || needsReconfiguration} // Allow connections if configured or needs reconfiguration
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
              style={{ right: '-4px' }} // Position slightly outward
            />
          </div>
        </div>
      </div>

      <AdderConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={needsReconfiguration || needsPartialReconfiguration || data.isApiConfigured === false}
        initialData={{ 
          name, 
          inputA_col_name,
          inputB_col_name,
          sum_column_name,
          inputNodes: getInputNodeNames(),
          position: getNodePosition(),
          isConfigured: isConfigured
        }}
      />
    </>
  );
};

export default AdderNode;